import React, { useContext, useState, useEffect, useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { PeriodKey, useDashboard } from '../../../../contexts/DashboardContext';
import LoadingPlaceholder from '../../../../muiComponents/LoadingPlaceholder';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import useTranslate from '../../../../customHooks/useTranslate';
import { MenuItem, Select } from '@mui/material';
import {
    formatPeriodSubtitle,
    getCommittedData,
} from '../../../../utils/dashboard/dashbordUtils';
import useLanguage from '../../../../customHooks/useLanguage';
import styles from '../../../../stylesheets/variables.module.scss';
import { capitalize } from '../../../../utils/usefulFunctions';

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const BookingHistoryChart = () => {
    const { bookingsHistory, changeBookingsHistoryPeriod } = useDashboard();
    const translate = useTranslate();
    const { language } = useLanguage();
    const chartTitle = translate('history_bookings');

    const options: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: tooltipItems => {
                        const index = tooltipItems[0].dataIndex;
                        return data[index].longLabel;
                    },
                    label: tooltipItem => {
                        return `${translate('bookings')}: ${
                            tooltipItem.formattedValue
                        }`;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    drawOnChartArea: false,
                    drawTicks: false,
                },
                ticks: {
                    // beginAtZero: true,
                    font: {
                        size: 8,
                        family: styles.mainFont,
                    },
                    padding: 5,
                },
            },
            y: {
                stacked: true,
                grid: {
                    lineWidth: 0.5,
                    drawTicks: false,
                },
                ticks: {
                    // beginAtZero: true,
                    font: {
                        size: 8,
                        family: styles.mainFont,
                    },
                    padding: 5,
                    callback: label => {
                        if (typeof label === 'number') {
                            return Math.floor(label) === label ? label : null;
                        }
                        return label;
                    },
                },
            },
        },
    };

    const periodSubtitle = useMemo(() => {
        const subtitle = formatPeriodSubtitle(
            bookingsHistory.period.key,
            language,
            translate
        );
        return capitalize(subtitle);
    }, [bookingsHistory.period.key, language, translate]);

    const data = getCommittedData(
        bookingsHistory.data,
        bookingsHistory.period.key,
        language
    );

    return (
        <>
            <div className='flex flex-align-center flex-between'>
                <div>
                    <SectionTitle title={chartTitle} variant='h3' />
                    <p className='landing-chart__subtitle'>{periodSubtitle}</p>
                </div>
                <Select
                    size='small'
                    className='landing-chart__period-select'
                    value={bookingsHistory.period.key}
                    onChange={e =>
                        changeBookingsHistoryPeriod(e.target.value as PeriodKey)
                    }
                >
                    <MenuItem value={PeriodKey.CURRENT}>
                        {translate('current_month')}
                    </MenuItem>
                    <MenuItem value={PeriodKey.LAST_MONTH}>
                        {translate('last_month')}
                    </MenuItem>
                    <MenuItem value={PeriodKey.LAST_3_MONTHS}>
                        {translate('last_3_months')}
                    </MenuItem>
                    <MenuItem value={PeriodKey.LAST_6_MONTHS}>
                        {translate('last_6_months')}
                    </MenuItem>
                    <MenuItem value={PeriodKey.LAST_12_MONTHS}>
                        {translate('last_12_months')}
                    </MenuItem>
                </Select>
            </div>

            {bookingsHistory.isLoading ? (
                <LoadingPlaceholder
                    variant='rounded'
                    width={'100%'}
                    height={'100%'}
                />
            ) : bookingsHistory.isDataLoaded && bookingsHistory.data.length ? (
                <div className='flex-grow flex-center-center'>
                    <Bar
                        data={{
                            labels: data.map(item => item.label),
                            datasets: [
                                {
                                    label: translate('Bookings'),
                                    data: data.map(item => item.count),
                                    backgroundColor: styles.primaryBrigth,
                                },
                            ],
                        }}
                        options={options}
                    />
                </div>
            ) : (
                <div className='flex-grow flex-center-center'>
                    {translate('no_data_for_period')}
                </div>
            )}
        </>
    );
};

export default BookingHistoryChart;
